<template v-if="MAPS_API_KEY && mostRecentLocation">
  <SkeletonLoader
    v-if="mapLoading"
    class="w-full h-52 rounded-2xl"
  />
  <GoogleMap
    ref="mapRef"
    :api-key="MAPS_API_KEY"
    map-id="fc0959b7dc4da895"
    class="malomo-map"
    :center="center"
    :zoom="14"
    :class="hideMap && 'hidden'"
    disable-default-ui
    :clickable-icons="false"
  >
    <MalomoMapInfo
      v-if="showInfo && location"
      :options="{ position: location }"
      v-bind="{ shipment, mostRecentLocation }"
      @closeclick="showInfo = false"
    />
    <MalomoMapMarker
      v-if="location"
      :options="{ position: location }"
      v-bind="{ shipment }"
      @click="showInfo = true"
    />
  </GoogleMap>
</template>

<script setup lang="ts">
import { GoogleMap } from 'vue3-google-map'
import { geocode, MAPS_API_KEY } from '@/services/maps'
import SkeletonLoader from '@/components/shared/v2/skeleton-loader.vue'

import type { Shipment } from '@/types'
import MalomoMapInfo from './malomo-map-info.vue'
import MalomoMapMarker from './malomo-map-marker.vue'
import { computed, ref, watch } from 'vue'

export interface Props {
  shipment: Shipment
}

const props = defineProps<Props>()

const mostRecentLocation = computed(() =>
  props.shipment.scan_events.find(s =>
    s.location.city
    && s.location.state
    && s.location.postal_code
  )
)

const mapLoading = ref(true)
const mapRef = ref()
const location = ref()
const showInfo = ref(true)

const hideMap = computed(() => !location.value)

const DEFAULT_COORDINATES = {
  lat: 40.689247, // Statue of Liberty, NY
  lng: -74.044502
}

const center = DEFAULT_COORDINATES

watch(() => mapRef.value?.map, async (map) => {
  if (!map || !mostRecentLocation.value) return
  
  const { city, state, postal_code } = mostRecentLocation.value.location
  const address = `${city}, ${state}, ${postal_code}`
  try {
    const coordinates = await geocode(address)
    location.value = coordinates
    map.setCenter(coordinates)
  } catch (error) {
    console.error('Error geocoding address:', error)
  } finally {
    mapLoading.value = false
  }
}, { immediate: true })
</script>

<style>
.malomo-map {
  @apply h-52 relative;
  > div {
    @apply rounded-2xl;
  }
}
</style>
